// Email-template "types" and their variable presets.
//
// A type one-click declares the variables a template can use and feeds the
// "Insert variable" picker. The backend (templateengine.Engine) validates every
// `{{.path}}` against the declared `variables` before rendering: a declared
// `type:"map"` variable authorises any nested field under it, and a missing map
// key renders as `<no value>` rather than failing. So declaring the top-level
// containers as maps keeps a typed template render-safe.

export const DEFAULT_TEMPLATE_TYPE = "generic";

// Ordered map of type-key -> { label, declarations, tokens }.
//  - declarations: persisted into `variables.variables` (what the backend validates)
//  - tokens:       drives the insert picker / sample preview (frontend-only)
export const EMAIL_TEMPLATE_TYPES = {
  generic: {
    label: "Generic",
    declarations: [],
    tokens: [],
  },
  form_submission: {
    label: "Form Submission",
    declarations: [
      {
        name: "form",
        type: "map",
        description: "The submitted form (form_id, name, description).",
        required: false,
      },
      {
        name: "website",
        type: "map",
        description: "The website the form belongs to (name, domain).",
        required: false,
      },
      {
        name: "submission",
        type: "map",
        description: "The submission record (form_submission_id, created_at).",
        required: false,
      },
      {
        name: "submitted_data",
        type: "map",
        description: "The values the visitor submitted — keys depend on the form's schema.",
        required: false,
      },
    ],
    tokens: [
      { label: "Form name", path: "form.name", sample: "Membership Application" },
      {
        label: "Form description",
        path: "form.description",
        sample: "Apply to join the World Startup Federation",
      },
      {
        label: "Form ID",
        path: "form.form_id",
        sample: "f47ac10b-58cc-4372-a567-0e02b2c3d479",
      },
      { label: "Website name", path: "website.name", sample: "World Startup Federation" },
      { label: "Website domain", path: "website.domain", sample: "worldstartupfederation.org" },
      {
        label: "Submission ID",
        path: "submission.form_submission_id",
        sample: "9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d",
      },
      {
        label: "Submitted at",
        path: "submission.created_at",
        format: "02 Jan 2006, 3:04 PM",
        sample: "19 May 2026, 10:00 AM",
      },
      { label: "Submitted field…", base: "submitted_data", dynamic: true },
    ],
  },
};

// Merge a type's declarations into an existing variables array, non-destructively
// and idempotently — existing entries are kept verbatim; only declarations whose
// `name` is absent are appended (in the form's card shape, with raw scalars).
export const mergeDeclarations = (existing = [], declarations = []) => {
  const names = new Set(existing.map((v) => v.name));
  const additions = declarations
    .filter((d) => !names.has(d.name))
    .map((d) => ({
      name: d.name,
      type: d.type,
      description: d.description || "",
      example: "",
      default: null,
      required: !!d.required,
    }));
  return [...existing, ...additions];
};

// Build the token for a picker entry. A `dynamic` (map) entry takes a field name;
// an entry with a `format` is wrapped in the engine's FormatTime func; everything
// else uses its fixed `path`.
export const buildToken = (entry, field) => {
  if (entry.dynamic) return `{{.${entry.base}.${field}}}`;
  if (entry.format) return `{{FormatTime .${entry.path} "${entry.format}"}}`;
  return `{{.${entry.path}}}`;
};

// Substitute tokens with sample values for an approximate preview. Simple value
// tokens are replaced; bare control tags ({{if}}/{{range}}/{{else}}/{{end}}) are
// stripped (their inner content stays); anything else is left untouched.
export const renderSample = (text = "", sampleMap = {}) =>
  text
    .replace(/\{\{\s*(?:if|range|with|else\s+if|else|end)\b[^}]*\}\}/g, "")
    .replace(/\{\{\s*FormatTime\s+\.([\w.]+)\s+"[^"]*"\s*\}\}/g, (match, path) =>
      path in sampleMap ? sampleMap[path] : `[${path}]`
    )
    .replace(/\{\{\s*\.([\w.]+)\s*\}\}/g, (match, path) => {
      if (path in sampleMap) return sampleMap[path];
      if (path.startsWith("submitted_data.")) return `[${path.slice("submitted_data.".length)}]`;
      return `[${path}]`;
    });
